"use client";
import { cn } from "@/lib/utils";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import colorPalette from "tailwindcss/colors";
import { GradientPicker } from "../ui/gradient-picker";

import { Input } from "../ui/input";
import { DeleteIcon, PlayCircleIcon, SaveIcon } from "lucide-react";

import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Card, CardContent } from "../ui/card";
import {
  colors,
  defaultOpacities,
  defaultPatterns,
  defaultSizes,
} from "./event-theme-pattern";
import { ThemeFontPicker } from "./event-theme-font-picker";
import { Label } from "../ui/label";
import EventThemeApplyer from "./event-theme-applyer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  MoonIcon,
  SunIcon,
  SwitchIcon,
  UpdateIcon,
} from "@radix-ui/react-icons";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { errorHandler, patch } from "@/api/dashboard-auth.client";
import { safeJsonParse } from "@/lib/safeJsonParse";
import { useTheme } from "next-themes";
import { useSearchParams } from "next/navigation";

export default function EventThemeEditor({
  event,
  onSave,
  additionalCss,
}: any) {
  const searchParams = useSearchParams();

  const { theme, setTheme } = useTheme();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [mode, setMode] = useState("minimal");
  const [background, setBackground] = useState("");

  const [patternName, setPatternName] = useState(
    defaultPatterns[Math.floor(Math.random() * defaultPatterns.length)],
  );
  const [patternSize, setPatternSize] = useState("32");
  const [patternColor, setPatternColor] = useState(
    colors[Math.floor(Math.random() * colors.length)] + "-400",
  );
  const [patternSecondaryColor, setPatternSecondaryColor] = useState(
    colors[Math.floor(Math.random() * colors.length)] + "-200",
  );
  const [patternOpacity, setPatternOpacity] = useState("60");

  const [particlesType, setParticlesType] = useState("");
  const [emojis, setEmojis] = useState(["🍑", "😊", "🐙"]);

  const [backgroundVideo, setBackgroundVideo] = useState("RhlQvbvMg-0");

  const [fontPayload, setFontPayload] = useState("");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const eventListener = () => {
      setDrawerOpen(!drawerOpen);
    };
    window.addEventListener("toggle-event-theme-editor", eventListener);
    return () => {
      window.removeEventListener("toggle-event-theme-editor", eventListener);
    };
  }, [drawerOpen]);

  useEffect(() => {
    searchParams && searchParams?.get("editor") && setDrawerOpen(true);
  }, [searchParams]);

  useEffect(() => {
    if (event?.theme && safeJsonParse(event.theme)) {
      setMode(safeJsonParse(event.theme)?.mode);
      setBackground(safeJsonParse(event.theme)?.background);
      setPatternName(safeJsonParse(event.theme)?.pattern?.name);
      setPatternColor(safeJsonParse(event.theme)?.pattern?.color);
      setPatternSecondaryColor(
        safeJsonParse(event.theme)?.pattern?.secondaryColor,
      );
      setPatternOpacity(safeJsonParse(event.theme)?.pattern?.opacity);
      setParticlesType(safeJsonParse(event.theme)?.particles?.type);
      setEmojis(safeJsonParse(event.theme)?.particles?.emojis);
      setBackgroundVideo(safeJsonParse(event.theme)?.backgroundVideo);
      setFontPayload(safeJsonParse(event.theme)?.fontPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  function handleSubmit() {
    const payload = {
      mode: mode,
      background: background,
      fontPayload: fontPayload,
      backgroundVideo: backgroundVideo,
      pattern: {
        name: patternName,
        size: patternSize,
        opacity: patternOpacity,
        color: patternColor,
        secondaryColor: patternSecondaryColor,
      },
      particles: {
        type: particlesType,
        emojis: emojis,
      },
    };

    onSave && onSave(payload);
    if (!onSave) {
      setSaving(true);
      patch("/event/{id}", {
        params: {
          path: {
            id: event.id as string,
          },
        },
        body: {
          theme: payload,
        },
      }).then((fetchResponse: any) => {
        setSaving(false);
        if (fetchResponse.error) {
          errorHandler(fetchResponse.error);
        } else {
          setDrawerOpen(false);
        }
      });
    }
  }

  useEffect(() => {
    const payload = {
      mode: mode,
      background: background,
      fontPayload: fontPayload,
      backgroundVideo: backgroundVideo,
      pattern: {
        name: patternName,
        size: patternSize,
        opacity: patternOpacity,
        color: patternColor,
        secondaryColor: patternSecondaryColor,
      },
      particles: {
        type: particlesType,
        emojis: emojis,
      },
    };
    if (onSave) {
      onSave(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <>
      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        dismissible={false}
      >
        <DrawerContent
          className={cn(
            "no-overlay flex flex-col items-center bg-background/50 backdrop-blur border-none outline-none max-w-fit md:max-w-5xl m-auto px-4",
          )}
        >
          <div className="flex flex-col gap-4 py-5 overflow-hidden md:max-w-5xl md:w-4xl">
            <ScrollArea className="w-screen rounded-md md:w-full whitespace-nowrap ">
              <div className="flex flex-row justify-between w-full gap-2">
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "minimal" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("minimal");
                    setBackground(
                      "linear-gradient(to top left, #cc95c0, #dbd4b4, #7aa1d2)",
                    );
                    setTheme("light");
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-slate-200">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Minimal
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "sunrise" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("sunrise");
                    setBackground(
                      "linear-gradient(to top left, #FF5373, #FFC858, #ff7882, #6D3BFF)",
                    );
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-noneborder group-hover:shadow-md transition-all w-[90px] h-14 bg-gradient-to-tr from-teal-400 to-yellow-200">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Sunrise
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "warp" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("warp");
                    setBackground(
                      "linear-gradient(to top left, #0f0c29, #302b63, #24243e)",
                    );
                    setTheme("dark");
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-[url('/images/ui/theme-warp.jpg')] bg-cover">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Warp
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "emoji" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("emoji");
                    setParticlesType("collision");
                    setBackground(
                      "linear-gradient(to top left, #ffe29f, #ffa99f, #ff719a)",
                    );
                    setTheme("light");
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-[url('/images/ui/theme-emoji.jpg')] bg-cover">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Emoji
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "confetti" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("confetti");
                    setParticlesType("confetti");
                    setBackground(
                      "linear-gradient(to top left, #feac5e, #c779d0, #4bc0c8)",
                    );
                    setTheme("light");
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-[url('/images/ui/theme-confetti.jpg')] bg-cover">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Confetti
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "pattern" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("pattern");
                    setPatternName(
                      defaultPatterns[
                        Math.floor(Math.random() * defaultPatterns.length)
                      ],
                    );
                    setPatternColor(
                      colors[Math.floor(Math.random() * colors.length)] +
                        "-200",
                    );
                    setPatternSecondaryColor(
                      colors[Math.floor(Math.random() * colors.length)] +
                        "-400",
                    );
                  }}
                >
                  <Card
                    className="border group-hover:shadow-md transition-all w-[90px] h-14 pattern-zigzag pattern-blue-300 pattern-bg-background 
 pattern-size-6 pattern-opacity-100"
                  >
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Pattern
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "season" && "border-opacity-50",
                  )}
                  onClick={() => {
                    setMode("season");
                    setBackground(
                      "linear-gradient(to top left, #1e3c72, #1e3c72, #2a5298)",
                    );
                    setParticlesType("snow");
                  }}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-[url('/images/ui/theme-season.jpg')] bg-cover">
                    <CardContent className="h-full p-0"></CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Season
                  </div>
                </div>
                <div
                  className={cn(
                    "flex flex-col group cursor-pointer gap-1 p-1 rounded-lg border-2 border-slate-500 border-opacity-0",
                    mode == "video" && "border-opacity-50",
                  )}
                  onClick={() => setMode("video")}
                >
                  <Card className="shadow-sm hover:shadow-md border-none border group-hover:shadow-md transition-all w-[90px] h-14 bg-gradient-to-tr from-slate-900 to-slate-500">
                    <CardContent className="flex items-center justify-center w-full h-full p-0">
                      <PlayCircleIcon
                        className="text-white"
                        strokeWidth={1.5}
                      />
                    </CardContent>
                  </Card>
                  <div className="w-full text-xs text-center transition-all opacity-50 group-hover:opacity-100">
                    Video
                  </div>
                </div>
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <div className="flex flex-col w-full gap-2 px-5 md:flex-row md:px-0">
              {[
                "minimal",
                "warp",
                "sunrise",
                "emoji",
                "confetti",
                "season",
              ].includes(mode) && (
                <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                  <Label className="opacity-50">Background</Label>
                  <GradientPicker
                    background={background}
                    setBackground={setBackground}
                    className="w-full h-10 bg-background/30"
                    type={
                      mode == "minimal"
                        ? "all"
                        : mode == "sunrise"
                          ? "gradient&solid"
                          : mode == "warp"
                            ? "gradient"
                            : mode == "emoji"
                              ? "gradient"
                              : mode == "confetti"
                                ? "gradient"
                                : mode == "season"
                                  ? "gradient"
                                  : "all"
                    }
                  />
                </div>
              )}
              {mode == "video" && (
                <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                  <Label className="opacity-50">Youtube video Background</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size={"lg"}
                        className="justify-start w-full bg-background/30"
                      >
                        youtu.be/{backgroundVideo}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="grid grid-cols-2 gap-2 p-2">
                      <div
                        onClick={() => setBackgroundVideo("RhlQvbvMg-0")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/RhlQvbvMg-0/hqdefault.jpg)] bg-cover bg-center"
                      ></div>{" "}
                      <div
                        onClick={() => setBackgroundVideo("qLMTSPEOFLI")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/qLMTSPEOFLI/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div
                        onClick={() => setBackgroundVideo("ppaWghHNqMA")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/ppaWghHNqMA/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div
                        onClick={() => setBackgroundVideo("U1j-eO3-aR8")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/U1j-eO3-aR8/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div
                        onClick={() => setBackgroundVideo("aKISl-crHUo")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/aKISl-crHUo/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div
                        onClick={() => setBackgroundVideo("yz-uktixvKQ")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/yz-uktixvKQ/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div
                        onClick={() => setBackgroundVideo("L3677Z6S4KY")}
                        className="h-16 w-full text-center p-3 bg-slate-500/10 rounded-md cursor-pointer bg-[url(http://i3.ytimg.com/vi/L3677Z6S4KY/hqdefault.jpg)] bg-cover bg-center"
                      ></div>
                      <div className="flex items-center w-full max-w-md col-span-2 overflow-hidden border rounded-md -800 bg-background">
                        <div className="flex flex-row items-center gap-1 px-2 text-sm text-muted-foreground">
                          https://youtu.be/
                        </div>
                        <Input
                          className="flex-1 bg-transparent border-none border-b text-sm px-0 opacity-50 mb-0.5"
                          id="videoId"
                          placeholder="Video ID"
                          type="videoid"
                          tabIndex={-1}
                          value={backgroundVideo}
                          onChange={(e) => setBackgroundVideo(e.target.value)}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              {mode == "pattern" && (
                <>
                  <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                    <Label className="opacity-50">Background</Label>
                    <Select
                      onValueChange={setPatternSecondaryColor}
                      defaultValue={patternSecondaryColor}
                    >
                      <SelectTrigger className={`w-full bg-background/30 h-10`}>
                        <div
                          className={`bg-${patternSecondaryColor} h-4 w-4 rounded-sm`}
                        ></div>
                        <SelectValue placeholder="Secondary Color" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <div className="flex flex-row-reverse gap-x-1">
                            {Object.entries(colorPalette).map(
                              ([key, value]) => {
                                if (
                                  typeof value === "object" &&
                                  colors.includes(key)
                                ) {
                                  return (
                                    <div
                                      className="flex flex-col overflow-hidden rounded-sm"
                                      key={key}
                                    >
                                      {Object.entries(value).map(
                                        ([shadeKey, shadeValue]) => (
                                          <SelectItem
                                            value={
                                              `${key}-${shadeKey}` as string
                                            }
                                            key={`${key}-${shadeKey}`}
                                            className={`bg-${key}-${shadeKey} h-4 w-4 p-0 hover:opacity-50 focus:bg-${key}-${shadeKey} text-transparent focus:text-transparent hover:text-transparent cursor-pointer rounded-none `}
                                          >{`${key}-${shadeKey}`}</SelectItem>
                                        ),
                                      )}
                                    </div>
                                  );
                                }
                              },
                            )}
                          </div>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                    <Label className="opacity-50">Style</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size={"lg"}
                          className="left-0 justify-start w-full gap-2 pl-4 overflow-hidden capitalize bg-background/30"
                        >
                          <div
                            className={`bg-${patternColor} h-4 w-4 rounded-sm`}
                          ></div>{" "}
                          {patternName}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="grid grid-cols-2 gap-2 p-2">
                        <div className="grid w-full w-md items-center gap-1.5">
                          <Label className="opacity-50">Pattern</Label>
                          <Select
                            onValueChange={setPatternName}
                            defaultValue={patternName}
                          >
                            <SelectTrigger className="w-full ">
                              <SelectValue placeholder="Pattern" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {defaultPatterns.map(
                                  (pattern: string, idx: number) => (
                                    <SelectItem
                                      value={pattern}
                                      key={idx}
                                      className="capitalize"
                                    >
                                      {pattern}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid w-full w-md items-center gap-1.5">
                          <Label className="opacity-50">Alternate Color</Label>
                          <Select
                            onValueChange={setPatternColor}
                            defaultValue={patternColor}
                          >
                            <SelectTrigger
                              className={`w-full content-start gap-2 bg-background/30`}
                            >
                              <div
                                className={`bg-${patternColor} h-4 w-4 rounded-sm`}
                              ></div>
                              <SelectValue
                                placeholder="Primary Color"
                                className="content-start"
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <div className="flex flex-row-reverse gap-x-1">
                                  {Object.entries(colorPalette).map(
                                    ([key, value]) => {
                                      if (
                                        typeof value === "object" &&
                                        colors.includes(key)
                                      ) {
                                        return (
                                          <div
                                            className="flex flex-col overflow-hidden rounded-sm"
                                            key={key}
                                          >
                                            {Object.entries(value).map(
                                              ([shadeKey, shadeValue]) => (
                                                <SelectItem
                                                  value={
                                                    `${key}-${shadeKey}` as string
                                                  }
                                                  key={`${key}-${shadeKey}`}
                                                  className={`bg-${key}-${shadeKey} h-4 w-4 p-0 hover:opacity-50 focus:bg-${key}-${shadeKey} text-transparent focus:text-transparent hover:text-transparent cursor-pointer rounded-none`}
                                                >{`${key}-${shadeKey}`}</SelectItem>
                                              ),
                                            )}
                                          </div>
                                        );
                                      }
                                    },
                                  )}
                                </div>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid w-full w-md items-center gap-1.5">
                          <Label className="opacity-50">Size</Label>
                          <Select
                            onValueChange={setPatternSize}
                            defaultValue={patternSize}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {defaultSizes.map(
                                  (size: number, idx: number) => (
                                    <SelectItem
                                      value={size?.toString()}
                                      key={idx}
                                      className="capitalize"
                                    >
                                      {size}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid w-full w-md items-center gap-1.5">
                          <Label className="opacity-50">Opacity</Label>
                          <Select
                            onValueChange={setPatternOpacity}
                            defaultValue={patternOpacity}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Opacity" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {defaultOpacities.map(
                                  (opacity: number, idx: number) => (
                                    <SelectItem
                                      value={opacity?.toString()}
                                      key={idx}
                                      className="capitalize"
                                    >
                                      {opacity}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}
              {["emoji", "confetti", "season"].includes(mode) && (
                <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                  <Label className="opacity-50">Style</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size={"lg"}
                        className="justify-start w-full capitalize bg-background/30"
                      >
                        {particlesType} {["emoji"].includes(mode) && emojis}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="grid grid-cols-1 gap-2 p-2">
                      <Label>Type</Label>

                      <Select
                        onValueChange={setParticlesType}
                        defaultValue={particlesType}
                      >
                        <SelectTrigger className="w-full bg-background/30">
                          <SelectValue placeholder="Particles" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {mode == "confetti" && (
                              <>
                                <SelectItem
                                  value={"fireworks"}
                                  className="capitalize"
                                >
                                  Fireworks
                                </SelectItem>
                                <SelectItem
                                  value={"confetti"}
                                  className="capitalize"
                                >
                                  Confetti
                                </SelectItem>
                                <SelectItem
                                  value={"fountain"}
                                  className="capitalize"
                                >
                                  Fountain
                                </SelectItem>
                              </>
                            )}
                            {mode == "emoji" && (
                              <>
                                <SelectItem
                                  value={"collision"}
                                  className="capitalize"
                                >
                                  Collision
                                </SelectItem>
                                <SelectItem
                                  value={"messy"}
                                  className="capitalize"
                                >
                                  Messy
                                </SelectItem>
                              </>
                            )}
                            {mode == "season" && (
                              <SelectItem value={"snow"} className="capitalize">
                                Snow
                              </SelectItem>
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {["emoji"].includes(mode) && (
                        <>
                          <Label>Emoji</Label>

                          <div className="flex flex-row gap-1">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  size={"lg"}
                                  className="justify-start w-full p-1 overflow-hidden text-3xl bg-background/30"
                                >
                                  {emojis || "Choose emoji"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="grid grid-cols-1 p-2 gap-2 w-[370px] bg-transparent border-none shadow-none">
                                <Picker
                                  navPosition="bottom"
                                  previewPosition="none"
                                  data={data}
                                  onEmojiSelect={(value: any) =>
                                    setEmojis((prevItems) => [
                                      ...prevItems,
                                      value.native,
                                    ])
                                  }
                                />
                              </PopoverContent>
                            </Popover>
                            <Button
                              variant="outline"
                              className="justify-start p-2 w-fit bg-background/30"
                              size={"icon"}
                              onClick={() =>
                                setEmojis((prevItems) => prevItems.slice(0, -1))
                              }
                            >
                              <DeleteIcon className="w-5 h-5 opacity-50" />
                            </Button>
                          </div>
                        </>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              {!["pattern", "confetti", "emoji", "season"].includes(mode) && (
                <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                  <Label className="opacity-50">Style</Label>
                  <Input
                    className="w-full h-10 bg-background/30"
                    disabled
                    placeholder="Style"
                  />
                </div>
              )}
              <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                <Label className="opacity-50">Font</Label>
                <ThemeFontPicker
                  fontPayload={fontPayload}
                  setFontPayload={setFontPayload}
                />
              </div>
              <div className="flex flex-col w-full gap-2 m-0 overflow-hidden md:w-1/4">
                <Label className="opacity-50">Theme</Label>
                <div className="flex flex-row gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size={"lg"}
                        className="w-1/2 border bg-background/30"
                      >
                        {theme == "dark" ? (
                          <MoonIcon className="w-4 h-4 " />
                        ) : theme == "light" ? (
                          <SunIcon className="w-4 h-4 " />
                        ) : (
                          <SwitchIcon className="w-4 h-4 mr-2" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setTheme("light");
                        }}
                      >
                        <SunIcon className="w-4 h-4 mr-2" />
                        Light
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setTheme("dark");
                        }}
                      >
                        <MoonIcon className="w-4 h-4 mr-2" />
                        Dark
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setTheme("system");
                        }}
                      >
                        <SwitchIcon className="w-4 h-4 mr-2" />
                        Auto
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="default"
                    size={"lg"}
                    className="w-1/2 group"
                    onClick={handleSubmit}
                    disabled={saving}
                  >
                    {saving ? (
                      <UpdateIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      <SaveIcon className="w-4 h-4" strokeWidth={1.5} />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <EventThemeApplyer
        event={event}
        additionalCss={event?.additionalCss}
        theme={{
          mode: mode,
          background: background,
          fontPayload: fontPayload,
          backgroundVideo: backgroundVideo,
          pattern: {
            name: patternName,
            size: patternSize,
            opacity: patternOpacity,
            color: patternColor,
            secondaryColor: patternSecondaryColor,
          },
          particles: {
            type: particlesType,
            emojis: emojis,
          },
        }}
      />
    </>
  );
}
